import { useEffect, useState, useCallback, useRef } from "react";
import { Coins, Plus, X, Trash2, Pencil, Paperclip, ArrowUpCircle, ArrowDownCircle, FileSpreadsheet, RefreshCw, ListTree } from "lucide-react";
import api from "../../lib/api";
import { errText } from "../../lib/errors";
import { toast } from "sonner";
import { downloadApi } from "../../lib/download";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";

const tl = (n) => (n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = "h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 w-full";
const today = new Date().toISOString().slice(0, 10);
const thisMonth = today.slice(0, 7);
const EMPTY_FORM = { type: "gider", category: "Genel", amount: "", date: today, description: "", account_id: "", receipt_url: "" };
const EMPTY_ITEM = { type: "gider", name: "", default_amount: "", recurring: false, day_of_month: 1 };

export default function AdminCashflow() {
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [month, setMonth] = useState(thisMonth);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [cfItems, setCfItems] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [newItem, setNewItem] = useState(EMPTY_ITEM);
  const [stmt, setStmt] = useState(null);
  const [series, setSeries] = useState([]);
  const [busy, setBusy] = useState(false);
  const receiptRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: accs }, { data: items }, { data: cmp }] = await Promise.all([
        api.get("/admin/cashflow", { params: { month } }),
        api.get("/admin/bank-accounts"),
        api.get("/admin/cashflow-items"),
        api.get("/admin/cashflow/compare", { params: { months: 6 } }),
      ]);
      setData(d);
      setAccounts(accs);
      setCfItems(items);
      setSeries(cmp);
    } catch { toast.error("Kayıtlar yüklenemedi"); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error("Geçerli tutar girin"); return; }
    setBusy(true);
    try {
      if (editId) await api.put(`/admin/cashflow/${editId}`, { ...form, amount });
      else await api.post("/admin/cashflow", { ...form, amount });
      toast.success(editId ? "Kayıt güncellendi" : "Kayıt eklendi");
      setForm(null); setEditId(null);
      load();
    } catch (e) { toast.error(errText(e) || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const openEdit = (e) => {
    setForm({ type: e.type, category: e.category, amount: e.amount, date: e.date, description: e.description || "", account_id: e.account_id || "", receipt_url: e.receipt_url || "" });
    setEditId(e.entry_id);
  };

  const remove = async (e) => {
    if (!window.confirm("Kayıt silinsin mi? (Bağlı banka hareketi silinmez)")) return;
    try {
      await api.delete(`/admin/cashflow/${e.entry_id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(errText(err) || "Silinemedi"); }
  };

  const addItem = async () => {
    if (!newItem.name.trim()) { toast.error("Kalem adı zorunlu"); return; }
    try {
      await api.post("/admin/cashflow-items", { ...newItem, default_amount: parseFloat(newItem.default_amount) || 0, day_of_month: parseInt(newItem.day_of_month) || 1 });
      toast.success("Hesap eklendi");
      setNewItem(EMPTY_ITEM);
      load();
    } catch (e) { toast.error(errText(e) || "Eklenemedi"); }
  };

  const removeItem = async (it) => {
    if (!window.confirm(`"${it.name}" hesabı silinsin mi? (Geçmiş kayıtlar etkilenmez)`)) return;
    try {
      await api.delete(`/admin/cashflow-items/${it.item_id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(errText(e) || "Silinemedi"); }
  };

  const genRecurring = async () => {
    try {
      const { data: r } = await api.post("/admin/cashflow/generate-recurring", null, { params: { month } });
      if (r.created > 0) toast.success(`${r.created} tekrarlayan kayıt ${r.month} için oluşturuldu`);
      else toast.info("Bu ay için oluşturulacak yeni tekrarlayan kayıt yok");
      load();
    } catch { toast.error("Oluşturulamadı"); }
  };

  const openStatement = async (it) => {
    try {
      const { data: s } = await api.get(`/admin/cashflow-items/${it.item_id}/statement`);
      setStmt(s);
    } catch { toast.error("Ekstre yüklenemedi"); }
  };

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "fis");
    try {
      const { data: up } = await api.post("/admin/upload", fd);
      const url = up.url || up.file?.url || up.file_url;
      if (url) { setForm((f) => ({ ...f, receipt_url: url })); toast.success("Fiş yüklendi"); }
    } catch { toast.error("Fiş yüklenemedi"); }
    e.target.value = "";
  };

  const downloadExcel = () => downloadApi(`/admin/reports/gelir-gider.xlsx?month=${month}`, `gelir-gider-${month}.xlsx`).catch((e) => toast.error(e.message || "Rapor oluşturulamadı"));

  const [pz, setPz] = useState({ start: "", end: "" });
  const [pzData, setPzData] = useState(null);
  const [pzBusy, setPzBusy] = useState(false);

  const calcPZ = async () => {
    setPzBusy(true);
    try {
      const params = {};
      if (pz.start) params.start = pz.start;
      if (pz.end) params.end = pz.end;
      const { data } = await api.get("/admin/reports/kar-zarar", { params });
      setPzData(data);
    } catch { toast.error("Kar/Zarar hesaplanamadı"); }
    finally { setPzBusy(false); }
  };

  const s = data?.summary;
  const items = data?.items || [];
  const formItems = form ? cfItems.filter((i) => i.type === form.type) : [];

  return (
    <div data-testid="admin-cashflow-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">Gelir / Gider Takibi</h1>
          <p className="text-stone-500 text-sm mt-1">Gelir ve gider hesapları, nakit giriş/çıkış kayıtları; hesap seçilirse banka/kasa bakiyesine de işlenir.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input data-testid="cashflow-month" type="month" className={`${inputCls} !w-auto`} value={month} onChange={(e) => setMonth(e.target.value)} />
          <button data-testid="cashflow-excel" onClick={downloadExcel} className="flex items-center gap-2 border border-forest text-forest hover:bg-forest/5 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"><FileSpreadsheet className="w-4 h-4" /> Excel</button>
          <button data-testid="cashflow-items-manage" onClick={() => setManageOpen(true)} className="flex items-center gap-2 bg-white border border-stone-300 hover:border-forest text-stone-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"><ListTree className="w-4 h-4" /> Gelir/Gider Hesapları</button>
          <button data-testid="cashflow-new-button" onClick={() => { setForm({ ...EMPTY_FORM }); setEditId(null); }} className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"><Plus className="w-4 h-4" /> Yeni Kayıt</button>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div data-testid="cashflow-income" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">{s.month} Gelir</p><p className="text-2xl font-bold text-emerald-700 mt-1">{tl(s.income)} ₺</p></div>
          <div data-testid="cashflow-expense" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">{s.month} Gider</p><p className="text-2xl font-bold text-red-600 mt-1">{tl(s.expense)} ₺</p></div>
          <div data-testid="cashflow-net" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm"><p className="text-xs text-stone-500 uppercase tracking-wide">Net</p><p className={`text-2xl font-bold mt-1 ${s.net >= 0 ? "text-forest" : "text-red-600"}`}>{tl(s.net)} ₺</p></div>
        </div>
      )}

      {series.length > 0 && (
        <div data-testid="cashflow-chart" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
          <p className="text-sm font-semibold text-stone-700 mb-3">Aylık Karşılaştırma (son {series.length} ay)</p>
          <div className="h-56 min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series}>
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => `${tl(v)} ₺`} />
                <Legend />
                <Bar dataKey="gelir" name="Gelir" fill="#047857" radius={[4, 4, 0, 0]} />
                <Bar dataKey="gider" name="Gider" fill="#DC2626" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {s?.by_category?.length > 0 && (
        <div data-testid="cashflow-categories" className="flex flex-wrap gap-2">
          {s.by_category.map((c) => (
            <span key={c.category} className="bg-white border border-stone-200 rounded-xl px-3.5 py-2 text-xs text-stone-700"><b>{c.category}</b>: <span className="text-emerald-700">+{tl(c.gelir)}</span> / <span className="text-red-600">-{tl(c.gider)}</span></span>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-xs uppercase font-bold tracking-wider text-stone-600 bg-[#EDE8DE] border-b border-stone-300">
            <th className="px-5 py-3">Tarih</th><th className="px-5 py-3">Tür</th><th className="px-5 py-3">Hesap/Kategori</th><th className="px-5 py-3">Açıklama</th><th className="px-5 py-3 text-right">Tutar</th><th className="px-5 py-3 text-right">İşlem</th>
          </tr></thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.entry_id} data-testid={`cashflow-row-${e.entry_id}`} className="border-b border-stone-100">
                <td className="px-5 py-3 text-stone-600">{e.date}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${e.type === "gelir" ? "text-emerald-700" : "text-red-600"}`}>
                    {e.type === "gelir" ? <ArrowUpCircle className="w-4 h-4" /> : <ArrowDownCircle className="w-4 h-4" />}
                    {e.type === "gelir" ? "Gelir" : "Gider"}
                  </span>
                </td>
                <td className="px-5 py-3"><span className="px-2 py-1 rounded-lg bg-stone-100 text-xs font-medium">{e.category}</span></td>
                <td className="px-5 py-3 text-stone-700">
                  <span className="inline-flex items-center gap-1.5">{e.description || "-"}
                    {e.receipt_url && <a href={e.receipt_url} target="_blank" rel="noreferrer" data-testid={`cashflow-receipt-${e.entry_id}`} className="text-forest hover:text-forest-dark" title="Fişi görüntüle"><Paperclip className="w-3.5 h-3.5" /></a>}
                  </span>
                </td>
                <td className={`px-5 py-3 text-right font-bold ${e.type === "gelir" ? "text-emerald-700" : "text-red-600"}`}>{e.type === "gelir" ? "+" : "-"}{tl(e.amount)} ₺</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button data-testid={`cashflow-edit-${e.entry_id}`} onClick={() => openEdit(e)} className="p-1.5 hover:bg-cream-dark rounded-lg"><Pencil className="w-4 h-4 text-stone-500" /></button>
                  <button data-testid={`cashflow-delete-${e.entry_id}`} onClick={() => remove(e)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="px-5 py-10 text-center text-stone-400"><Coins className="w-8 h-8 mx-auto mb-2 opacity-40" />Bu ay kayıt yok</td></tr>}
          </tbody>
        </table>
      </div>

      <div data-testid="kar-zarar-card" className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-semibold text-stone-900">Kar / Zarar Hesabı</h3>
          <input data-testid="pz-start" type="date" className={`${inputCls} !w-auto`} value={pz.start} onChange={(e) => setPz({ ...pz, start: e.target.value })} />
          <input data-testid="pz-end" type="date" className={`${inputCls} !w-auto`} value={pz.end} onChange={(e) => setPz({ ...pz, end: e.target.value })} />
          <button data-testid="pz-calc" onClick={calcPZ} disabled={pzBusy} className="bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors">{pzBusy ? "Hesaplanıyor..." : "Hesapla"}</button>
          <span className="text-[11px] text-stone-400">Tarih boş bırakılırsa tüm kayıtlar hesaplanır</span>
        </div>
        {pzData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div data-testid="pz-gelir" className="bg-emerald-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-emerald-800 uppercase">Toplam Gelir</p>
              <p className="text-xl font-bold text-emerald-700 mt-1">{tl(pzData.gelir.total)} ₺</p>
              <div className="mt-2 space-y-1">{Object.entries(pzData.gelir.by_category).map(([k, v]) => <p key={k} className="text-xs text-emerald-900 flex justify-between"><span>{k}</span><b>{tl(v)} ₺</b></p>)}</div>
            </div>
            <div data-testid="pz-gider" className="bg-red-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-red-800 uppercase">Toplam Gider</p>
              <p className="text-xl font-bold text-red-600 mt-1">{tl(pzData.gider.total)} ₺</p>
              <div className="mt-2 space-y-1">{Object.entries(pzData.gider.by_category).map(([k, v]) => <p key={k} className="text-xs text-red-900 flex justify-between"><span>{k}</span><b>{tl(v)} ₺</b></p>)}</div>
            </div>
            <div data-testid="pz-net" className={`rounded-xl p-4 ${pzData.net >= 0 ? "bg-forest text-cream" : "bg-red-600 text-white"}`}>
              <p className="text-xs font-semibold uppercase opacity-70">Net {pzData.net >= 0 ? "Kar" : "Zarar"}</p>
              <p className="text-2xl font-bold mt-1">{tl(Math.abs(pzData.net))} ₺</p>
              <p className="text-xs opacity-70 mt-1">{pzData.start || "—"} → {pzData.end || "—"}</p>
            </div>
          </div>
        )}
      </div>

      {form && (
        <div data-testid="cashflow-form-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => { setForm(null); setEditId(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">{editId ? "Kaydı Düzenle" : "Yeni Gelir / Gider"}</h3>
              <button data-testid="cashflow-form-close" onClick={() => { setForm(null); setEditId(null); }} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex gap-2 mb-4">
              {[["gelir", "Gelir"], ["gider", "Gider"]].map(([v, l]) => (
                <button key={v} data-testid={`cashflow-type-${v}`} onClick={() => setForm({ ...form, type: v })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${form.type === v ? (v === "gelir" ? "bg-emerald-600 text-white" : "bg-red-600 text-white") : "bg-stone-100 text-stone-600"}`}>{l}</button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* SOL: kayıt bilgileri */}
              <div className="space-y-3 sm:pr-6 sm:border-r sm:border-stone-200">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Kayıt Bilgileri</p>
                {formItems.length > 0 && (
                  <div><label className="text-xs font-semibold text-stone-600">Hesap seç (otomatik doldurur)</label>
                    <select data-testid="cashflow-form-item" className={inputCls} value="" onChange={(e) => {
                      const it = formItems.find((x) => x.item_id === e.target.value);
                      if (it) setForm({ ...form, category: it.name, amount: it.default_amount || form.amount });
                    }}>
                      <option value="">Hesap seç...</option>
                      {formItems.map((i) => <option key={i.item_id} value={i.item_id}>{i.name}</option>)}
                    </select></div>
                )}
                <div><label className="text-xs font-semibold text-stone-600">Kategori</label><input data-testid="cashflow-form-category" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Genel, Kira, Kargo..." /></div>
                <div><label className="text-xs font-semibold text-stone-600">Tutar ₺ *</label><input data-testid="cashflow-form-amount" type="number" min="0" step="0.01" className={inputCls} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
                <div><label className="text-xs font-semibold text-stone-600">Tarih</label><input data-testid="cashflow-form-date" type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              </div>
              {/* SAĞ: bağlantı & belge */}
              <div className="space-y-3">
                <p className="text-[11px] uppercase tracking-wider font-bold text-stone-400">Bağlantı & Belge</p>
                <div><label className="text-xs font-semibold text-stone-600">Hesap (ops.)</label>
                  <select data-testid="cashflow-form-account" className={inputCls} value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })}>
                    <option value="">Sadece kayıt</option>
                    {accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
                  </select></div>
                <div><label className="text-xs font-semibold text-stone-600">Açıklama</label><input data-testid="cashflow-form-desc" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div>
                  <label className="text-xs font-semibold text-stone-600">Fiş / Belge (ops.)</label>
                  <div className="flex items-center gap-2 mt-1">
                    <button type="button" data-testid="cashflow-receipt-upload" onClick={() => receiptRef.current?.click()} className="flex items-center gap-2 bg-white border border-stone-300 hover:border-forest text-stone-700 text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors"><Paperclip className="w-3.5 h-3.5" /> Fiş Yükle</button>
                    <input ref={receiptRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={uploadReceipt} data-testid="cashflow-receipt-input" />
                    {form.receipt_url && <a href={form.receipt_url} target="_blank" rel="noreferrer" data-testid="cashflow-receipt-view" className="text-xs font-semibold text-forest underline">Yüklendi — Görüntüle</a>}
                  </div>
                </div>
              </div>
            </div>
            <button data-testid="cashflow-form-submit" onClick={save} disabled={busy} className="mt-5 w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{busy ? "Kaydediliyor..." : editId ? "Güncelle" : "Kaydet"}</button>
          </div>
        </div>
      )}

      {manageOpen && (
        <div data-testid="cashflow-items-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setManageOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-forest">Gelir / Gider Hesapları</h3>
              <button data-testid="cashflow-items-close" onClick={() => setManageOpen(false)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="bg-stone-50 rounded-xl p-4 space-y-3 mb-5">
              <p className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Yeni Hesap</p>
              <div className="flex gap-2">
                {[["gelir", "Gelir"], ["gider", "Gider"]].map(([v, l]) => (
                  <button key={v} data-testid={`item-type-${v}`} onClick={() => setNewItem({ ...newItem, type: v })} className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${newItem.type === v ? (v === "gelir" ? "bg-emerald-600 text-white" : "bg-red-600 text-white") : "bg-white border border-stone-200 text-stone-600"}`}>{l}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="item-name" className={inputCls} placeholder="Hesap adı * (örn. Kira, Maaş, Satış)" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
                <input data-testid="item-amount" type="number" min="0" step="0.01" className={inputCls} placeholder="Varsayılan tutar ₺" value={newItem.default_amount} onChange={(e) => setNewItem({ ...newItem, default_amount: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-xs text-stone-600">
                <input data-testid="item-recurring" type="checkbox" checked={newItem.recurring} onChange={(e) => setNewItem({ ...newItem, recurring: e.target.checked })} className="rounded" />
                Her ay otomatik tekrarlasın, ayın
                <input data-testid="item-day" type="number" min="1" max="28" className="w-16 h-8 px-2 rounded-lg border border-stone-300 text-xs" value={newItem.day_of_month} onChange={(e) => setNewItem({ ...newItem, day_of_month: e.target.value })} />
                . günü
              </label>
              <button data-testid="item-add" onClick={addItem} className="w-full bg-forest hover:bg-forest-dark text-white text-sm font-semibold py-2.5 rounded-xl transition-colors">Hesap Ekle</button>
            </div>

            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Tanımlı Hesaplar ({cfItems.length})</p>
              <button data-testid="items-generate-recurring" onClick={genRecurring} className="flex items-center gap-1.5 text-xs font-semibold text-forest hover:bg-forest/5 px-3 py-1.5 rounded-lg transition-colors"><RefreshCw className="w-3.5 h-3.5" /> Tekrarlayanları Oluştur ({month})</button>
            </div>
            <div className="divide-y divide-stone-100">
              {cfItems.map((it) => (
                <div key={it.item_id} data-testid={`item-row-${it.item_id}`} className="flex items-center justify-between gap-2 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-800 flex items-center gap-2">{it.name}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${it.type === "gelir" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>{it.type === "gelir" ? "Gelir" : "Gider"}</span>
                      {it.recurring && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Tekrarlayan</span>}
                    </p>
                    <p className="text-xs text-stone-400">{it.default_amount ? `Varsayılan: ${tl(it.default_amount)} ₺` : "Tutar serbest"}{it.recurring ? ` • Her ay ${it.day_of_month}. gün` : ""}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`item-statement-${it.item_id}`} onClick={() => openStatement(it)} className="text-xs font-semibold text-forest hover:bg-forest/5 px-3 py-2 rounded-lg transition-colors">Ekstre / Hareketler</button>
                    <button data-testid={`item-delete-${it.item_id}`} onClick={() => removeItem(it)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
                  </div>
                </div>
              ))}
              {cfItems.length === 0 && <p className="py-6 text-center text-sm text-stone-400">Henüz hesap tanımlanmadı</p>}
            </div>
          </div>
        </div>
      )}

      {stmt && (
        <div data-testid="cashflow-statement-modal" className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setStmt(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="font-serif text-xl text-forest">{stmt.item.name} — Ekstre</h3>
                <p className="text-xs text-stone-500 mt-1">{stmt.item.type === "gelir" ? "Gelir" : "Gider"} hesabı · {stmt.entries.length} hareket</p>
              </div>
              <button data-testid="cashflow-statement-close" onClick={() => setStmt(null)} className="p-1.5 hover:bg-stone-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-emerald-50 rounded-xl p-3"><p className="text-[10px] uppercase tracking-wide text-emerald-700">Toplam Giriş</p><p className="font-bold text-emerald-800">+{tl(stmt.total_in)} ₺</p></div>
              <div className="bg-red-50 rounded-xl p-3"><p className="text-[10px] uppercase tracking-wide text-red-700">Toplam Çıkış</p><p className="font-bold text-red-700">-{tl(stmt.total_out)} ₺</p></div>
              <div className="bg-stone-100 rounded-xl p-3"><p className="text-[10px] uppercase tracking-wide text-stone-500">Net</p><p className={`font-bold ${stmt.net >= 0 ? "text-forest" : "text-red-600"}`}>{tl(stmt.net)} ₺</p></div>
            </div>
            <div className="divide-y divide-stone-100">
              {stmt.entries.map((e) => (
                <div key={e.entry_id} data-testid={`stmt-row-${e.entry_id}`} className="py-2.5 flex items-center gap-3 text-sm">
                  <span className="text-stone-400 text-xs w-24 shrink-0">{e.date}</span>
                  <span className="flex-1 min-w-0 truncate text-stone-700">{e.description || "-"}</span>
                  <span className={`font-semibold ${e.signed >= 0 ? "text-emerald-700" : "text-red-600"}`}>{e.signed >= 0 ? "+" : ""}{tl(e.signed)} ₺</span>
                  <span className="text-xs text-stone-400 w-24 text-right shrink-0">{tl(e.running)} ₺</span>
                </div>
              ))}
              {stmt.entries.length === 0 && <p className="py-8 text-center text-sm text-stone-400">Bu hesapta henüz hareket yok</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
